import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Avatar,
  Button,
  useTheme,
  useMediaQuery,
  Stack,
  IconButton,
} from '@mui/material';
import {
  People,
  EmojiEvents,
  Handshake,
  BarChart,
  CheckCircle,
  LinkedIn,
  Twitter,
  Email,
  TrendingUp,
  Security,
  Speed,
} from '@mui/icons-material';
import SEOHead from '../components/SEOHead';

const About: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const stats = [
    { number: '500+', label: 'Satisfied Clients' },
    { number: '5+', label: 'Years Experience' },
    { number: '50+', label: 'Expert Team Members' },
    { number: '99%', label: 'Success Rate' },
  ];

  const values = [
    {
      icon: <Handshake sx={{ fontSize: 40, color: '#123D6B' }} />,
      title: 'Integrity',
      description: 'We uphold the highest standards of integrity in all of our actions.',
    },
    {
      icon: <BarChart sx={{ fontSize: 40, color: '#123D6B' }} />,
      title: 'Excellence',
      description: 'We strive for excellence in all we do, delivering quality service and results.',
    },
    {
      icon: <People sx={{ fontSize: 40, color: '#123D6B' }} />,
      title: 'Client Focus',
      description: 'We put our clients at the center of everything we do.',
    },
    {
      icon: <EmojiEvents sx={{ fontSize: 40, color: '#123D6B' }} />,
      title: 'Innovation',
      description: 'We embrace innovation to provide cutting-edge financial solutions.',
    },
  ];

  const team = [
    {
      name: 'Shubham Khandelwal',
      role: 'Co-Founder & CEO',
      image: '/shubham-khandelwal.png',
      bio: 'A strategic direct tax and corporate framework architect with mastery in structural financial engineering, cross-border fiscal strategy, and statutory compliance frameworks.',
      linkedin: 'https://www.linkedin.com/in/shubhamkhandelwalfingard', 
      twitter: 'https://x.com/m_cmashubham',
      email: 'mailto:coo.fingard@gmail.com',
    },
    {
      name: 'Shubham Singh Rathore',
      role: 'Co-Founder & CMO',
      image: '/shubham-singh-rathore.png',
      bio: 'Driving strategic corporate growth, client advisory frameworks, and institutional brand transformations.',
      linkedin: 'https://www.linkedin.com/in/shubham-singh-rathore-05871a1a2',
      twitter: 'https://twitter.com/fingard',
      email: 'mailto:cmo.fingard@gmail.com',
    },
    {
      name: 'Garvika Sethi',
      role: 'Finance Advisory Head',
      image: '', 
      bio: 'Financial strategist specializing in corporate accounting structures, auditing, and outsourced CFO tracking.',
      linkedin: 'https://www.linkedin.com',
      twitter: 'https://twitter.com',
      email: 'mailto:finance@fingard.in',
    },
    {
      name: 'Chirag Mojidra',
      role: 'Head of Taxation',
      image: '/chirag-moidra.png', 
      bio: 'Leading statutory representations, litigation advisory, and optimized direct/GST tax compliances.',
      linkedin: 'https://www.linkedin.com',
      twitter: 'https://twitter.com',
      email: 'mailto:taxation@fingard.in',
    },
    {
      name: 'Muskan Sharma',
      role: 'Head of Corporate Compliance',
      image: '',
      bio: 'Specializing in customized portfolio tracking, direct fiscal analysis, and systemic cash flow execution.',
      linkedin: 'https://www.linkedin.com',
      twitter: 'https://twitter.com',
      email: 'mailto:muskan@fingard.in',
    },
  ];

  return (
    <Box sx={{ bgcolor: '#F8FAFC' }}>
      <SEOHead
        title="About Fingard Partners | Trusted Chartered Accountants"
        description="Learn about Fingard Partners LLP — a trusted CA firm delivering expert tax filing, compliance, and advisory services to businesses and individuals across India."
        keywords="About Fingard Partners, Chartered Accountant firm, CA firm India, Tax consultants"
        canonicalUrl="/about"
      />
      {/* Hero Section */}
      <Box sx={{ background: 'linear-gradient(135deg, #123D6B 0%, #051424 100%)', color: 'white', py: { xs: 8, md: 14 }, textAlign: 'center' }}>
        <Container maxWidth="lg">
          <Typography variant={isMobile ? 'h3' : 'h2'} component="h1" sx={{ fontWeight: 800, mb: 3, letterSpacing: '-0.02em' }}>About Fingard</Typography>
          <Typography variant="h5" sx={{ maxWidth: 800, mx: 'auto', opacity: 0.85, fontWeight: 400, fontSize: { xs: '1.1rem', md: '1.3rem' }, lineHeight: 1.6 }}>Your Trusted Partner in Finance & Compliance</Typography>
          
          <Box sx={{ mt: 5, display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CheckCircle sx={{ color: '#C59B27' }} /><Typography variant="body2" sx={{ fontWeight: 600 }}>5+ Years Experience</Typography></Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CheckCircle sx={{ color: '#C59B27' }} /><Typography variant="body2" sx={{ fontWeight: 600 }}>500+ Happy Clients</Typography></Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CheckCircle sx={{ color: '#C59B27' }} /><Typography variant="body2" sx={{ fontWeight: 600 }}>99% Success Rate</Typography></Box>
          </Box>
        </Container>
      </Box>

      {/* Story Section */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h4" component="h2" sx={{ fontWeight: 800, mb: 3, color: '#123D6B', letterSpacing: '-0.01em' }}>Our Story</Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '15px', lineHeight: 1.7, color: '#475569', mb: 3 }}>
                Founded in 2025, Fingard has grown from a specialized tax consultancy firm to a full-service financial and compliance solutions infrastructure provider. Our journey has been driven by a consistent commitment to excellence, strict corporate integrity, and premium client execution.
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '15px', lineHeight: 1.7, color: '#64748B', mb: 4 }}>
                With an integrated network of certified practitioners, we provide comprehensive financial advisory to growth ventures and corporates across India. Our partner-driven approach and deep multi-sector regulatory intelligence make us a prominent trusted registry in the corporate ecosystem.
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Security sx={{ color: '#123D6B', fontSize: 18 }} /><Typography variant="caption" sx={{ fontWeight: 600, color: '#475569' }}>Certified Professionals</Typography></Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><TrendingUp sx={{ color: '#123D6B', fontSize: 18 }} /><Typography variant="caption" sx={{ fontWeight: 600, color: '#475569' }}>Proven Track Record</Typography></Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Speed sx={{ color: '#123D6B', fontSize: 18 }} /><Typography variant="caption" sx={{ fontWeight: 600, color: '#475569' }}>Fast & Reliable</Typography></Box>
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button component={RouterLink} to="/services" variant="contained" disableElevation size="large" sx={{ bgcolor: '#123D6B', color: '#FFFFFF', px: 4, py: 1.5, borderRadius: '8px', fontWeight: 600, textTransform: 'none', '&:hover': { bgcolor: '#C59B27' } }}>Our Services</Button>
                <Button component={RouterLink} to="/contact" variant="outlined" size="large" sx={{ color: '#123D6B', borderColor: '#123D6B', px: 4, py: 1.5, borderRadius: '8px', fontWeight: 600, textTransform: 'none', '&:hover': { borderColor: '#C59B27', color: '#C59B27' } }}>Get Started</Button>
              </Stack>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box component="img" src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=800&q=80" alt="Fingard Financial Team" sx={{ width: '100%', height: 'auto', borderRadius: '16px', border: '1px solid #E2E8F0', boxShadow: '0px 20px 40px rgba(15, 23, 42, 0.02)' }} />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: '#FFFFFF', borderTop: '1px solid #E2E8F0', borderBottom: '1px solid #E2E8F0' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {stats.map((stat, idx) => (
              <Grid item xs={6} sm={3} key={idx} sx={{ textAlign: 'center' }}>
                <Typography variant="h3" sx={{ fontWeight: 800, color: '#123D6B', mb: 1, letterSpacing: '-0.02em' }}>{stat.number}</Typography>
                <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 600 }}>{stat.label}</Typography>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Core Values Section */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#123D6B', mb: 1.5 }}>Our Core Values</Typography>
            <Typography variant="body1" sx={{ color: '#64748B' }}>The principles that guide our work and define our financial culture</Typography>
          </Box>
          <Grid container spacing={4}>
            {values.map((value, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Paper variant="outlined" sx={{ p: 4, height: '100%', borderRadius: '16px', borderColor: '#E2E8F0', bgcolor: '#FFFFFF', textAlign: 'center' }}>
                  <Box sx={{ mb: 2.5 }}>{value.icon}</Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#123D6B', mb: 1.5, fontSize: '17px' }}>{value.title}</Typography>
                  <Typography variant="body2" sx={{ color: '#64748B', lineHeight: 1.6 }}>{value.description}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Team Profile Grid Section */}
      <Box sx={{ py: 10, bgcolor: '#FFFFFF', borderTop: '1px solid #E2E8F0' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#123D6B', mb: 1.5 }}>Meet Our Team</Typography>
            <Typography variant="body1" sx={{ color: '#64748B' }}>Dedicated professionals committed to your financial success</Typography>
          </Box>
          
          <Grid container spacing={3} justifyContent="center">
            {team.map((member, index) => (
              <Grid item xs={12} sm={6} md={2.4} key={index}>
                <Paper variant="outlined" sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', borderRadius: '16px', borderColor: '#E2E8F0', bgcolor: '#FFFFFF', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-4px)', borderColor: '#C59B27' } }}>
                  
                  {/* UPDATED: Stripped square boundaries to process a beautiful circular geometry border map */}
                  <Avatar 
                    alt={member.name} 
                    src={member.image} 
                    sx={{ 
                      width: 110, 
                      height: 110, 
                      mb: 2.5, 
                      border: '3px solid #123D6B',
                      boxShadow: '0px 8px 16px rgba(11, 40, 71, 0.06)'
                    }} 
                  />
                  
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#123D6B', fontSize: '14px', lineHeight: 1.3, mb: 0.5 }}>{member.name}</Typography>
                  <Typography variant="caption" sx={{ color: '#C59B27', fontWeight: 600, display: 'block', mb: 2 }}>{member.role}</Typography>
                  <Typography variant="body2" sx={{ color: '#64748B', fontSize: '12px', lineHeight: 1.6, flexGrow: 1, mb: 2 }}>{member.bio}</Typography>
                  
                  <Stack direction="row" spacing={0.5} justifyContent="center" sx={{ borderTop: '1px solid #F1F5F9', pt: 1, width: '100%' }}>
                    <IconButton size="small" component="a" href={member.linkedin} target="_blank" rel="noopener noreferrer" sx={{ color: '#123D6B' }}><LinkedIn sx={{ fontSize: 18 }} /></IconButton>
                    <IconButton size="small" component="a" href={member.twitter} target="_blank" rel="noopener noreferrer" sx={{ color: '#123D6B' }}><Twitter sx={{ fontSize: 18 }} /></IconButton>
                    <IconButton size="small" component="a" href={member.email} sx={{ color: '#123D6B' }}><Email sx={{ fontSize: 18 }} /></IconButton>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Corporate Call To Action Footer Banner */}
      <Box sx={{ py: 10, background: 'linear-gradient(135deg, #123D6B 0%, #051424 100%)', color: 'white', textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 2, letterSpacing: '-0.01em' }}>Ready to Work With Us?</Typography>
          <Typography variant="body1" sx={{ mb: 5, opacity: 0.85, maxWidth: 600, mx: 'auto', lineHeight: 1.6 }}>Whether you need help with tax planning, compliance, or financial advisory, our team is here to help you achieve your goals.</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button component={RouterLink} to="/contact" variant="contained" disableElevation sx={{ bgcolor: '#C59B27', color: '#FFFFFF', px: 5, py: 1.5, borderRadius: '8px', fontWeight: 600, textTransform: 'none', '&:hover': { bgcolor: '#A47E1F' } }}>Get In Touch</Button>
            <Button component={RouterLink} to="/services" variant="outlined" sx={{ color: '#FFFFFF', borderColor: '#FFFFFF', px: 5, py: 1.5, borderRadius: '8px', fontWeight: 600, textTransform: 'none', '&:hover': { bgcolor: 'rgba(255,255,255,0.05)', borderColor: '#C59B27', color: '#C59B27' } }}>View Services</Button>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default About;
